import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  ClassifierFilterDto,
  ClassifiersDataSource,
  CustomPagination,
  DirectoryHeader,
  Main,
  Padding,
  serializeValidPeriod,
  deserializeValidPeriod,
  Stack,
  TotalEntries,
  useDirectory,
  useListClassifiersQuery,
  serializeValidPeriodSort,
  EMPTY_PAGE,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { ClassifierDirectoryTable } from './ClassifierDirectoryTable';
import { convertToClassifiersDataSource } from './converters';
import { Alert } from 'antd';
import * as Icon from 'react-feather';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ClassifierDirectory.translations.json';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

const { addTranslations, useTranslation } = i18nNsFactory(
  'classifier-directory',
);
addTranslations(translations);

const FILTER_KEYS: (keyof ClassifierFilterDto)[] = [
  'code',
  'description',
  'inUse',
  'name',
  'search',
  'validFrom',
  'validTo',
  'hasCategories',
];

export const ClassifierDirectory = () => {
  const { t } = useTranslation();

  const navigateToClassifierCreate = useNavigateTo(path.classifierCreate);
  const {
    headerProps,
    tableProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialSort: [{ id: 'code', desc: false }],
    serializeFilter: serializeValidPeriod,
    deserializeFilter: deserializeValidPeriod,
    serializeSort: serializeValidPeriodSort,
  });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  // Fetch classifiers
  const {
    data: { content: classifiers, totalElements } = EMPTY_PAGE,
    isFetching: isClassifiersFetching,
  } = useListClassifiersQuery(queryParameters, {
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  const dataSource: ClassifiersDataSource[] = useMemo(
    () =>
      (classifiers ?? []).map((classifier) =>
        convertToClassifiersDataSource(classifier),
      ),
    [classifiers],
  );

  return (
    <>
      <Helmet>
        <title>{t('title.directory')}</title>
      </Helmet>
      <Main>
        <Main.Col border={false}>
          <Main.Content padding={false} id="classifier-directory-content">
            {errorMessage && (
              <Alert
                type="error"
                message={errorMessage}
                icon={<Icon.AlertTriangle />}
                banner
                closable
                afterClose={() => setErrorMessage(undefined)}
              />
            )}
            <DirectoryWrapper>
              <DirectoryHeader
                {...headerProps}
                title={t('title.directory')}
                infoIconUrl={InfoIconUrl.CLASSIFIER_DIRECTORY}
              />
              <Padding horizontal bottom>
                <ClassifierDirectoryTable
                  data={dataSource}
                  onCreate={navigateToClassifierCreate}
                  isLoading={isClassifiersFetching}
                  {...tableProps}
                />
              </Padding>
            </DirectoryWrapper>
          </Main.Content>
          <Main.Footer>
            <TotalEntries count={totalElements} />
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
      </Main>
    </>
  );
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const DirectoryWrapper = styled(Stack)`
  width: 100%;
  max-width: 100em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
